import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  input,
  model,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { TranslatePipe } from '@ngx-translate/core';

let nextSwitchId = 0;

@Component({
  selector: 'app-switch',
  imports: [TranslatePipe],
  templateUrl: './switch.html',
  styleUrl: './switch.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => Switch),
      multi: true,
    },
  ],
})
export class Switch implements ControlValueAccessor {
  readonly id = input<string>(`switch-${nextSwitchId++}`);
  readonly label = input<string>('');
  readonly hint = input<string>();
  readonly disabled = input<boolean>(false);

  readonly checked = model<boolean>(false);

  readonly #cvaDisabled = signal<boolean>(false);

  readonly isFocused = signal<boolean>(false);

  #onChange: (value: boolean) => void = () => {};
  #onTouched: () => void = () => {};

  protected readonly isDisabled = computed(() => this.disabled() || this.#cvaDisabled());

  writeValue(value: boolean): void {
    this.checked.set(!!value);
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.#onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.#onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.#cvaDisabled.set(isDisabled);
  }

  protected onNativeChange(event: Event): void {
    if (this.isDisabled()) {
      return;
    }

    const next = (event.target as HTMLInputElement).checked;
    this.#applyChecked(next);
  }

  protected onFocus(): void {
    this.isFocused.set(true);
  }

  protected onBlur(): void {
    this.isFocused.set(false);
    this.#onTouched();
  }

  #applyChecked(next: boolean): void {
    this.checked.set(next);
    this.#onChange(next);
  }
}
